import { SCV_MSG_CHANNEL_NAME } from '@main/config/scv.config';
import { ControllerFactory } from '@main/factories/controller.factory';
import { Constants, publishEvent } from '@salesforce/scv-connector-base';
import { RepositoryFactory } from '@main/factories/repository.factory';
import { CallTypes } from '@entities/call';

export type ScvMsgChannelEvent<TData> = {
  data: {
    type: string;
    data: TData;
  };
};

export type HvsWorkStartedPayload = {
  event?: string;
  component?: string;
  callId?: string;
  isError?: boolean;
};

export type ScvMsgPayload = {
  type: string;
  data: unknown;
};

export type CreateRecordParam = {
  type: string;
  phoneNumber: string;
  popupRecord: boolean;
  isAnonymous: boolean;
};

export type CreateRecordResult = {
  Id: string;
  Phone: string;
  Name?: string;
  Company?: string;
  FirstName?: string;
  LastName?: string;
};

export type SearchRecordParam = {
  popupRecord: boolean;
  isAnonymous: boolean;
  phoneNumberList?: string[];
  email?: string;
};
export type SearchRecordResult = CreateRecordResult | void;

/**
 * Fields name are capitalized due to salesforce naming convention
 */

export type SearchVoiceObjectParam = {
  VendorCallKey: string;
  VoiceCallId: string;
};
export type SearchVoiceObjectResult = {
  CallStartDateTime: string;
  CallType: string;
  Id: string;
  VendorCallKey: string;
  RelatedRecordId?: string;
  aircallscv2gp__Aircall_Voice__c: string | undefined;
  aircallscv2gp__Aircall_CallUuid__c: string | undefined;
};

export type SearchOrgAfterConversationWork = {
  HasAfterConvoWorkTimer: boolean;
};

export type AgentAvailabilityResult = {
  availableCapacity: boolean;
  message: string;
};

export type UpdateVoiceObjectParam = {
  VendorCallKey: string;
  aircallscv2gp__VendorCallUUIDKey__c: string;
  RelatedRecordId?: string;
  aircallscv2gp__Aircall_Voice__c?: string;
};
export type UpdateVoiceObjectResult = unknown;

export type UpdateVoiceObjectVendorKeyParam = {
  NewVendorCallKey: string;
  OldVendorCallKey: string;
};
export type UpdateVoiceObjectVendorKeyResult = unknown;

export type VoicemailOption = {
  id: string;
  name: string;
};

export class ScvObjectManager {
  private static channel = new BroadcastChannel(SCV_MSG_CHANNEL_NAME);
  static callRepository = RepositoryFactory.buildMemCallRepository();

  private static publishMsgToScv(payload: ScvMsgPayload) {
    publishEvent({
      eventType: 'MESSAGE',
      payload,
      registerLog: false,
    });
  }

  public static notifyScv(event: any) {
    this.publishMsgToScv(event);
  }

  private static promisify<T>(
    msgType: string,
    data: unknown,
    timeoutMs = 2500,
  ): Promise<T> {
    return new Promise<T>((res, rej) => {
      let settled = false;

      const eventListener = (msg: ScvMsgChannelEvent<T>) => {
        const {
          data: { type, data },
        } = msg;
        if (type === `${msgType}_FULFILLED`) {
          settled = true;
          this.channel.removeEventListener('message', eventListener);
          console.log(
            `SCV Connector: Received FULFILLED for ${msgType}:`,
            data,
          );
          res(data);
        } else if (type === `${msgType}_REJECTED`) {
          settled = true;
          this.channel.removeEventListener('message', eventListener);
          console.error(
            `SCV Connector: Received REJECTED for ${msgType}:`,
            data,
          );
          rej(
            `Error while ${msgType
              .toLowerCase()
              .replace('scv.', '')
              .replace(/_/g, ' ')}: ${JSON.stringify(data)}`,
          );
        }
      };

      this.channel.addEventListener('message', eventListener);
      this.publishMsgToScv({ type: msgType, data });

      setTimeout(() => {
        if (settled) return;
        this.channel.removeEventListener('message', eventListener);
        rej(`Timed out waiting for ${msgType} response after ${timeoutMs}ms`);
      }, timeoutMs);
    });
  }

  private static async handleVoicemailOptionSelected(data: any) {
    const storedCalls = await this.callRepository.getCalls();

    if (!storedCalls?.length) {
      return;
    }

    const voicemailDropCall = storedCalls.find(
      (call) => call.name === 'ForVoiceMailDrop',
    );

    if (!voicemailDropCall) {
      return;
    }

    try {
      const controller = ControllerFactory.buildVoiceMailDropController();

      const { prerecorded_message_id } = data as {
        prerecorded_message_id: string;
      };

      const result = await controller.handle({
        call_sid: voicemailDropCall.id,
        prerecorded_message_id,
      });

      if (result?.success) {
        console.log('SCV Connector: VoiceMailDrop succeeded');

        this.publishMsgToScv({
          type: 'SCV.VOICEMAIL_DROP_FULFILLED',
          data,
        });
      } else {
        console.warn('SCV Connector: VoiceMailDrop failed');

        this.publishMsgToScv({
          type: 'SCV.VOICEMAIL_DROP_REJECTED',
          data,
        });
      }
    } catch (error) {
      console.error('SCV Connector: Error executing VoiceMailDrop', error);

      this.publishMsgToScv({
        type: 'SCV.VOICEMAIL_DROP_REJECTED',
        data,
      });
    }
  }

  /**
   * Fetches the account's available tags and pushes them to the widget.
   * Fire-and-forget: triggered when wrap-up (ACW) starts; errors are logged and
   * never thrown back to the SDK so they cannot break ACW start.
   */
  static async fetchTags(): Promise<void> {
    try {
      const controller = ControllerFactory.buildFetchTagsController();
      await controller.handle();
    } catch (error) {
      console.error('SCV Connector: Error fetching tags', error);
    }
  }

  private static async handleTagSelected(data: any) {
    try {
      const { callId, tagIds } = data as { callId: string; tagIds: number[] };

      const controller = ControllerFactory.buildApplyTagsController();
      const result = await controller.handle({ callId, tagIds });

      if (result?.success) {
        console.log('SCV Connector: ApplyTags succeeded');

        this.publishMsgToScv({
          type: 'SCV.TAGS_APPLIED',
          data: { callId, tagIds },
        });
        this.publishMsgToScv({
          type: 'SCV.TAG_APPLIED_FULFILLED',
          data,
        });
      } else {
        console.warn('SCV Connector: ApplyTags failed');

        this.publishMsgToScv({
          type: 'SCV.TAG_APPLIED_REJECTED',
          data,
        });
      }
    } catch (error) {
      console.error('SCV Connector: Error executing ApplyTags', error);

      this.publishMsgToScv({
        type: 'SCV.TAG_APPLIED_REJECTED',
        data,
      });
    }
  }

  static async resolveCallDirection(
    callId?: string,
  ): Promise<'inbound' | 'outbound'> {
    const byId = callId
      ? await this.callRepository.fetchCallById(callId)
      : undefined;
    const active = byId ?? (await this.callRepository.getCurrentCall());

    if (!active) {
      console.warn(
        'SCV Connector: resolveCallDirection could not find call in state',
        { callId },
      );
      return 'outbound';
    }

    return active.type === CallTypes.INBOUND ? 'inbound' : 'outbound';
  }

  static async handleWrapUpEnded(data: unknown): Promise<void> {
    const payload = (data ?? {}) as {
      callId?: string;
      callStatus?: string;
      callResolution?: string;
    };

    if (!payload.callId) {
      console.warn(
        'SCV Connector: SCV.WRAP_UP_ENDED missing callId — skipping publish',
        data,
      );
      return;
    }

    const callType = await this.resolveCallDirection(payload.callId);

    publishEvent({
      eventType: Constants.VOICE_EVENT_TYPE.WRAP_UP_ENDED,
      payload: {
        callType,
        callId: payload.callId,
        callStatus: 'ended',
      },
      registerLog: false,
    });
  }

  /**
   * Entry point for VendorConnector#logMessageToVendor. Salesforce Softphone
   * has no onWorkStart-equivalent for partner LWCs — this LOG path is the
   * only supported way the connector learns of certain Softphone events, so
   * it doubles as an event source and gets routed to the relevant handler.
   */
  static handleVendorLog(
    logLevel: string,
    message: string,
    payload: unknown,
  ): void {
    const data = (payload ?? {}) as HvsWorkStartedPayload;
    const isSalesEngagementCall =
      data.event === 'HVS_WORK_STARTED' && data.component === 'WORK_QUEUE';

    if (isSalesEngagementCall) {
      this.handleSalesEngagementCallStarted(data);
    }
  }

  /**
   * Salesforce Softphone emits HVS_WORK_STARTED (via LOG) when a Sales
   * Engagement work-queue call starts — SCV equivalent of Open CTI onWorkStart.
   * Forward to the Salesforce package so wrap-up can require Call Resolution.
   */
  private static handleSalesEngagementCallStarted(
    data: HvsWorkStartedPayload,
  ): void {
    console.log('SCV Connector: Forwarding HVS_WORK_STARTED to SCV LWC', {
      callId: data.callId,
      component: data.component,
    });

    this.notifyScv({
      type: 'SCV.HVS_WORK_STARTED',
      data: {
        callId: data.callId || null,
        component: data.component || null,
      },
    });
  }

  private static initConnectorListeners() {
    this.channel.addEventListener(
      'message',
      async (msg: ScvMsgChannelEvent<unknown>) => {
        const {
          data: { type, data },
        } = msg;

        switch (type) {
          case 'SCV.VOICEMAIL_OPTION_SELECTED':
            await this.handleVoicemailOptionSelected(data);
            break;

          case 'SCV.FETCH_TAGS':
            await this.fetchTags();
            break;

          case 'SCV.TAG_SELECTED':
            await this.handleTagSelected(data);
            break;

          case 'SCV.WRAP_UP_ENDED':
            await this.handleWrapUpEnded(data);
            break;

          default:
            break;
        }
      },
    );
  }

  static {
    this.initConnectorListeners();
  }

  static async searchRecord(
    phoneNumber: string,
    popupRecord: boolean,
    anonymous = false,
  ): Promise<SearchRecordResult> {
    const msgType = 'SCV.SEARCH_RECORD';
    const data: SearchRecordParam = {
      popupRecord,
      isAnonymous: anonymous,
      phoneNumberList: [phoneNumber],
    };

    const recArr = await this.promisify<SearchRecordResult[]>(
      msgType,
      data,
    ).then((res) => {
      console.log('SCV Connector: Search related record response:', { res });
      return res;
    });
    if (!Array.isArray(recArr) || recArr.length === 0) {
      return;
    }
    return recArr[0];
  }

  static async createRecord(
    phoneNumber: string,
    recordType: string,
    popupRecord: boolean,
    anonymous = false,
  ): Promise<CreateRecordResult> {
    const msgType = 'SCV.CREATE_RECORD';
    const data: CreateRecordParam = {
      type: recordType,
      phoneNumber,
      popupRecord,
      isAnonymous: anonymous,
    };

    const recArr = await this.promisify<CreateRecordResult[]>(
      msgType,
      data,
    ).then((res) => {
      console.log('SCV Connector: Create new record response:', { res });
      return res;
    });
    return recArr[0];
  }

  static async checkAgentAvailability(): Promise<AgentAvailabilityResult> {
    const msgType = 'SCV.CHECK_AGENT_AVAILABILITY';
    const result = await this.promisify<AgentAvailabilityResult>(msgType, {});
    return result;
  }

  static async checkAfterConversationWork(): Promise<SearchOrgAfterConversationWork> {
    const msgType = 'SCV.SEARCH_IS_ACW_ENABLED';
    const data = {};

    const objArr = await this.promisify<SearchOrgAfterConversationWork>(
      msgType,
      data,
    ).then((res) => {
      console.log('search voice object', { res });
      return res;
    });
    return objArr;
  }

  static async searchVoiceObject(
    vendorCallKey: string,
    voiceCallId: string,
  ): Promise<SearchVoiceObjectResult> {
    const msgType = 'SCV.SEARCH_VOICE_OBJECT';
    const data: SearchVoiceObjectParam = {
      VendorCallKey: vendorCallKey,
      VoiceCallId: voiceCallId,
    };

    const objArr = await this.promisify<SearchVoiceObjectResult[]>(
      msgType,
      data,
    ).then((res) => {
      console.log('SCV Connector: Search voice object response:', { res });
      return res;
    });
    return objArr[0];
  }

  static async searchVoiceObjectForOutboundCall(
    vendorCallKey: string,
    voiceCallId: string,
  ): Promise<SearchVoiceObjectResult> {
    const msgType = 'SCV.SEARCH_VOICE_OBJECT';
    const data: SearchVoiceObjectParam = {
      VendorCallKey: vendorCallKey,
      VoiceCallId: voiceCallId,
    };
    const objArr = await this.promisify<SearchVoiceObjectResult[]>(
      msgType,
      data,
    ).then((res) => {
      const vcData: SearchVoiceObjectResult[] = [];
      console.log('search voice object', { res });
      if (Array.isArray(res)) {
        res.forEach((vc) => {
          if (vc.Id.includes(voiceCallId)) {
            vcData.push(vc);
          }
        });
      }
      return vcData;
    });
    return objArr[0];
  }

  static updateVoiceObject(
    vendorCallKey: string,
    callUuid: string,
    recordId?: string,
    aircallVoiceId?: string,
  ) {
    const msgType = 'SCV.UPDATE_VOICE_OBJECT';
    if (!vendorCallKey || vendorCallKey.includes('_')) {
      const callIds = vendorCallKey.split('_');
      callUuid = callIds[0];
    }
    const data: UpdateVoiceObjectParam = {
      RelatedRecordId: recordId,
      VendorCallKey: vendorCallKey,
      aircallscv2gp__VendorCallUUIDKey__c: callUuid,
      aircallscv2gp__Aircall_Voice__c: aircallVoiceId,
    };
    if (!recordId) delete data.RelatedRecordId;

    return this.promisify<UpdateVoiceObjectResult>(msgType, data);
  }

  static updateVoiceObjectVendorCallId(tempCallId: string, callUuid: string) {
    const msgType = 'SCV.UPDATE_VOICE_OBJECT';
    const data: UpdateVoiceObjectVendorKeyParam = {
      OldVendorCallKey: tempCallId,
      NewVendorCallKey: callUuid,
    };

    return this.promisify<UpdateVoiceObjectVendorKeyResult>(msgType, data);
  }
}
